import { type FC } from 'react';
import classNames from 'classnames/bind';
import { Theme } from '../types/enums';
import { FAIcon } from '../Icons/Icons';
import { NextgenImage } from '../NextgenImage';
import { type RawButtonPropsTypes } from './types/propsType';
import {
  ButtonIconPosition,
  ButtonSize,
  ButtonStyles,
  ButtonTextSize,
  ButtonVariant,
  ButtonWidth,
} from './types/enums';
import styles from './Button.module.scss';

const cx = classNames.bind(styles);
/**
 *  @description Button component provide <button></button> with basic styling;
 *  It can be used only through other Components.
 *  styling can be overriden through prop (className) only.
 *  @params props (RawButtonPropsTypes)
 */
const RawButton: FC<RawButtonPropsTypes> = (props: RawButtonPropsTypes) => {
  const {
    className = '',
    title,
    disabled = false,
    size = ButtonSize.MEDIUM,
    buttonStyle = ButtonStyles.SOLID,
    theme = Theme.BLACK,
    buttonWidth = ButtonWidth.FIT_CONTENT,
    icon,
    iconPosition,
    backgroundColor,
    buttonType,
    forwardedRef,
    isSensitiveField = false,
    customIcon,
    textSize = ButtonTextSize.DEFAULT,
    variant = ButtonVariant.DEFAULT,
    ...restProps
  } = props;
  const configs = ConfigService.getConfigs();
  const allowMasking = configs.NEXT_PUBLIC_ALLOW_MASKING_CHANGE;
  const { LEFT, RIGHT } = ButtonIconPosition;
  const iconPositionLeft = icon && iconPosition === LEFT;
  const iconPositionRight = icon && iconPosition === RIGHT;
  const CClassname = cx(
    'button',
    [size, buttonWidth, buttonStyle, theme],
    className,
    {
      disabled: disabled,
    },
    [buttonType],
    { 'small-text': textSize === ButtonTextSize.SM },
    { 'compact-padding': variant === ButtonVariant.COMPACT_PADDING },
  );
  const LeftIconClassname = cx('icon', 'icon-left', { 'small-icon': textSize === ButtonTextSize.SM });
  const RightIconClassname = cx('icon', 'icon-right', { 'small-icon': textSize === ButtonTextSize.SM });
  return (
    <button
      className={CClassname}
      ref={forwardedRef}
      style={
        backgroundColor
          ? {
              backgroundColor,
            }
          : {}
      }
      data-testid="button-test-id"
      disabled={disabled}
      data-dtrum-mask={allowMasking ? isSensitiveField : undefined}
      {...restProps}
    >
      <span className={styles['content']}>
        {customIcon ? (
          <NextgenImage
            data-testid="button-custom-icon-testid"
            src={customIcon}
            alt="button-custom-icon"
            height={16}
            width={16}
          />
        ) : (
          iconPositionLeft && <FAIcon {...icon} theme={theme} className={LeftIconClassname} />
        )}
        <span>{title}</span>
        {iconPositionRight && <FAIcon {...icon} theme={theme} className={RightIconClassname} />}
      </span>
    </button>
  );
};
RawButton.displayName = 'RawButton';
export default RawButton;
